import { useUpdateAccountPropertiesMutation } from "@api/accounts";
import Switch from "@components/ui/switch";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useAppDispatch } from "@store";
import { setDialogId } from "@store/global-slice";
import debounce from "lodash.debounce";
import moment from "moment";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { cn } from "@/lib/cn";
import { Button, Icon } from "@/shared";
import { useCurrentAccount } from "../providers/current-account-provider";
import type { AccessControlState } from "../types";
import { RadioSelect } from "./radio-select";

const options = [
  { title: "12 hours", value: 12 },
  { title: "24 hours", value: 24 },
  { title: "7 days", value: 168 },
  { title: "30 days", value: 720 },
];

export const AccessControl = () => {
  "use no memo";
  const dispatch = useAppDispatch();
  const [parent] = useAutoAnimate();
  const { account, isDifferentFromCurrentAccount } = useCurrentAccount();
  const [updateAccountProperties, { isLoading }] =
    useUpdateAccountPropertiesMutation();

  const { setValue, watch, handleSubmit } = useForm<AccessControlState>({
    defaultValues: {
      duration: null,
      reason: "",
      isOpen: false,
    },
  });

  const handleReasonChange = debounce((value: string) => {
    setValue("reason", value);
  }, 500);

  const onSubmit = async (data: AccessControlState) => {
    try {
      await updateAccountProperties({
        type: "access-control",
        payload: data,
        id: account?._id as string,
      }).unwrap();
      if (isDifferentFromCurrentAccount) {
        toast.success(`${account?.title} access control updated successfully`);
      } else {
        toast.success("Access control updated successfully");
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update access control");
    }
    dispatch(setDialogId({ id: null }));
  };

  const [duration, reason, isOpen] = watch(["duration", "reason", "isOpen"]);

  return (
    <>
      <div ref={parent}>
        <span className="tw-flex tw-items-center tw-justify-between tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
          Storydoc employees' access control
          <Switch>
            <Switch.Toggle
              checked={isOpen}
              onCheckedChange={() => setValue("isOpen", !isOpen)}
            />
          </Switch>
        </span>
        <DateUntilOpen />
        {isOpen && (
          <div className="tw-mt-6">
            <RadioSelect
              label="Duration"
              selected={duration}
              options={options}
              setSelected={(value) => {
                if (typeof value === "number") {
                  setValue("duration", value);
                }
              }}
            />
            <span className="tw-mb-1 tw-mt-6 tw-block tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
              Reason
            </span>
            <textarea
              onInput={(e) => {
                handleReasonChange(e.currentTarget.value);
              }}
              className={cn(
                "scrollbar tw-flex tw-h-24 tw-w-full tw-resize-none tw-items-center tw-justify-start tw-rounded tw-border tw-border-solid tw-border-[#e7e6ec] tw-bg-white tw-px-3 tw-py-1 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-base placeholder:tw-text-[#88819f] focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-2 focus:tw-outline-[#7b17cf33]",
                {
                  "tw-border-red-200 tw-bg-red-50/50": false,
                },
              )}
            ></textarea>
          </div>
        )}
        <Button
          size="large"
          variant="secondary"
          title="Update access"
          className="tw-mt-6"
          loading={isLoading}
          disabled={isOpen && (!duration || !reason)}
          onClick={() => handleSubmit(onSubmit)()}
        />
      </div>
      <hr className="tw-my-6 tw-border-t tw-border-blue-100" />
    </>
  );
};

const DateUntilOpen = () => {
  const { account } = useCurrentAccount();
  const isInvalid =
    !account?.openAccess ||
    moment().isAfter(moment.utc(account.openAccess).local());
  if (isInvalid) return null;

  return (
    <div className="tw-mt-6 tw-flex tw-items-center tw-justify-between">
      <span className="tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
        Open until
      </span>
      <div className="tw-flex tw-items-center tw-font-sans tw-text-[13px]/normal tw-font-normal tw-text-blue-base">
        <Icon icon="ri-calendar-2-fill" color="#88819F" className="tw-mr-2" />
        {account.openAccess &&
          moment.utc(account.openAccess).local().format("DD MMMM YYYY HH:mm")}
      </div>
    </div>
  );
};
